import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import {
  ORDERS_DECOPROF_EXCLUDED_SUPPLIERS,
  mapSupplierToType,
  maxOrderDecoprofStatus,
  type OrderDecoprofType,
  type OrderDecoprofStatus,
} from "@/lib/ordersDecoprof";


export interface BillingAddress {
  street?: string | null;
  number?: string | null;
  zipcode?: string | null;
  city?: string | null;
  country?: { title?: string | null } | null;
}

export interface OrdersDecoprofRow {
  ordernumber: string;
  orderid: number | null;
  primarySupplier: string | null;
  suppliers: string[];
  type: OrderDecoprofType;
  status: OrderDecoprofStatus;
  /** R52: createdat behouden zodat realtime-INSERTs op de juiste plek komen */
  createdAt: string | null;
  voornaam_klant: string | null;
  achternaam_klant: string | null;
  emailadres_klant: string | null;
  telefoonnummer_klant: string | null;
  customer_number: string | null;
  shipment_title: string | null;
  shippingdate: string | null;
  billing_address: BillingAddress | null;
  opmerking_klant: string | null;
}

interface RawRow {
  orderid: number | null;
  ordernumber: string | null;
  suppliertitle: string | null;
  producttitle: string | null;
  orders_decoprof_status: string | null;
  createdat: string | null;
  status: string | null;
  voornaam_klant: string | null;
  achternaam_klant: string | null;
  emailadres_klant: string | null;
  telefoonnummer_klant: string | null;
  customer_number: string | null;
  shipment_title: string | null;
  shippingdate: string | null;
  billing_address: BillingAddress | null;
  opmerking_klant: string | null;
}

// R57 — FIX 2: Lightspeed-statussen die "afgerond" betekenen. Orders met
// deze status verdwijnen automatisch uit de Decoprof-lijst (Mark's wens 4-mei).
// R72: "Geannuleerd" expliciet bevestigd op Mark's verzoek (mail 6-mei).
// Bron-bewijs (DB-distinct, supplier ≠ NULL):
//   packed_completed (754), completed_picked_up (15), completed_shipped (9),
//   cancelled (560 hist.) → uitsluiten. processing_* blijft tonen.
const LIGHTSPEED_COMPLETED_STATUSES = [
  "packed_completed",
  "completed_picked_up",
  "completed_shipped",
  "cancelled",
  // R72: defensieve aliassen voor toekomstige Lightspeed-varianten
  "canceled",
  "Geannuleerd",
] as const;
const LIGHTSPEED_COMPLETED_SET: ReadonlySet<string> = new Set(LIGHTSPEED_COMPLETED_STATUSES);

const QUERY_KEY = ["orders-decoprof"] as const;
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;
const MAX_VISIBLE_ORDERS = 2000;

const isPlaceholder = (row: RawRow) => row.producttitle === "No products";
const isWithinWindow = (createdat: string | null) => {
  if (!createdat) return true; // wees ruim — laat aggregator beslissen
  const t = Date.parse(createdat);
  if (Number.isNaN(t)) return true;
  return Date.now() - t <= SIXTY_DAYS_MS;
};

const createdAtMs = (value: string | null) => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const compareOrdersByCreatedAtDesc = (a: OrdersDecoprofRow, b: OrdersDecoprofRow) =>
  createdAtMs(b.createdAt) - createdAtMs(a.createdAt);

const insertSortedOrder = (list: OrdersDecoprofRow[], row: OrdersDecoprofRow) => {
  let low = 0;
  let high = list.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (compareOrdersByCreatedAtDesc(row, list[mid]) < 0) low = mid + 1;
    else high = mid;
  }
  list.splice(low, 0, row);
  if (list.length > MAX_VISIBLE_ORDERS) list.length = MAX_VISIBLE_ORDERS;
  return list;
};

export const useOrdersDecoprof = () => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();
  const qc = useQueryClient();
  const sinceIsoRef = useRef(new Date(Date.now() - SIXTY_DAYS_MS).toISOString());

  const query = useQuery({
    queryKey: QUERY_KEY,
    staleTime: 30_000,
    enabled: isAuthenticated && isSessionValid && !isValidating,
    queryFn: async (): Promise<OrdersDecoprofRow[]> => {
      const excludedList = Array.from(ORDERS_DECOPROF_EXCLUDED_SUPPLIERS);
      const excludedCsv = `(${excludedList.map((s) => `"${s}"`).join(",")})`;

      // R52: limit verlaagd van 10000 → 2000 (gecombineerd met virtualisatie)
      // R57 — FIX 2: filter Lightspeed-afgeronde orders weg (status NOT IN ...)
      const completedCsv = `(${LIGHTSPEED_COMPLETED_STATUSES.map((s) => `"${s}"`).join(",")})`;
      const { data, error } = await supabase
        .from("orders_live_ls")
        .select("orderid, ordernumber, suppliertitle, producttitle, orders_decoprof_status, createdat, status, voornaam_klant, achternaam_klant, emailadres_klant, telefoonnummer_klant, customer_number, shipment_title, shippingdate, billing_address, opmerking_klant")
        .gte("createdat", sinceIsoRef.current)
        .not("suppliertitle", "is", null)
        .neq("suppliertitle", "")
        .neq("producttitle", "No products")
        .not("suppliertitle", "in", excludedCsv)
        .not("status", "in", completedCsv)
        .order("createdat", { ascending: false })
        .limit(MAX_VISIBLE_ORDERS);

      if (error) {
        console.error("[useOrdersDecoprof] Error:", error);
        throw error;
      }

      const rows = (data ?? []) as RawRow[];
      return aggregateRows(rows);
    },
  });

  // ---- R52: Realtime channel — INSERT/UPDATE/DELETE patches in cache --------
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  useEffect(() => {
    if (!isAuthenticated || !isSessionValid || isValidating) return;

    const channel = supabase
      .channel("orders-decoprof-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "orders_live_ls" },
        (payload) => {
          const eventType = payload.eventType;
          const newRow = payload.new as RawRow | null;
          const oldRow = payload.old as RawRow | null;

          if (eventType === "DELETE") {
            const ordernumber = oldRow?.ordernumber;
            if (!ordernumber) return;
            qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) =>
              (prev ?? []).filter((o) => o.ordernumber !== ordernumber),
            );
            return;
          }

          if (!newRow || !newRow.ordernumber) return;
          const supplier = newRow.suppliertitle?.trim() || null;

          // Filter: alleen relevante (niet placeholder, niet uitgesloten supplier, binnen window)
          if (isPlaceholder(newRow)) return;
          if (!supplier) return;
          if (ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(supplier)) return;
          if (!isWithinWindow(newRow.createdat)) return;

          // FASE 2 punt 1 — pickup-shipment NIET meer hier filteren; UI-toggle
          // in OrdersDecoprofTab beslist of pickup-orders zichtbaar zijn.
          // De klant-allowlist (punt 2) wordt eveneens client-side in de UI toegepast.


          // R57 — FIX 2: als de Lightspeed-status nu "afgerond" is, verwijder
          // de order uit de cache (UPDATE-event waarbij order net afgerond
          // werd in Lightspeed). Pas dan ook geen INSERT toe.
          if (newRow.status && LIGHTSPEED_COMPLETED_SET.has(newRow.status)) {
            qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) =>
              (prev ?? []).filter((o) => o.ordernumber !== newRow.ordernumber),
            );
            return;
          }

          qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) => {
            const list = prev ? [...prev] : [];
            const idx = list.findIndex((o) => o.ordernumber === newRow.ordernumber);

            if (idx === -1) {
              // INSERT — nieuwe order op exacte createdAt-positie (DESC) i.p.v. blind prepend.
              const inserted: OrdersDecoprofRow = {
                ordernumber: newRow.ordernumber!,
                orderid: newRow.orderid ?? null,
                primarySupplier: supplier,
                suppliers: [supplier],
                type: mapSupplierToType(supplier),
                status: (newRow.orders_decoprof_status as OrderDecoprofStatus) || "Nieuw",
                createdAt: newRow.createdat ?? null,
                voornaam_klant: newRow.voornaam_klant ?? null,
                achternaam_klant: newRow.achternaam_klant ?? null,
                emailadres_klant: newRow.emailadres_klant ?? null,
                telefoonnummer_klant: newRow.telefoonnummer_klant ?? null,
                customer_number: newRow.customer_number ?? null,
                shipment_title: newRow.shipment_title ?? null,
                shippingdate: newRow.shippingdate ?? null,
                billing_address: newRow.billing_address ?? null,
                opmerking_klant: newRow.opmerking_klant ?? null,
              };
              // Flash-event voor UI
              if (typeof window !== "undefined") {
                window.dispatchEvent(
                  new CustomEvent("orders-decoprof:flash", {
                    detail: { ordernumber: newRow.ordernumber },
                  }),
                );
              }
              return insertSortedOrder(list, inserted);
            }

            // UPDATE / aanvullende INSERT-regel op bestaande order
            const existing = list[idx];
            const suppliers = existing.suppliers.includes(supplier)
              ? existing.suppliers
              : [...existing.suppliers, supplier];
            // Bug-fix: nooit downgraden. Een later toegevoegd line-item met
            // default "Nieuw" mag een al uitgeprinte order niet terugzetten.
            const incomingStatus = (newRow.orders_decoprof_status as OrderDecoprofStatus) || existing.status;
            const status = maxOrderDecoprofStatus(existing.status, incomingStatus);
            const updated: OrdersDecoprofRow = {
              ...existing,
              orderid: existing.orderid ?? newRow.orderid ?? null,
              suppliers,
              status,
              createdAt: existing.createdAt ?? newRow.createdat ?? null,
            };
            list.splice(idx, 1);
            return insertSortedOrder(list, updated);
          });
        },
      )
      .subscribe((status) => {
        console.log("[useOrdersDecoprof] realtime status:", status);
      });

    channelRef.current = channel;
    return () => {
      try { supabase.removeChannel(channel); } catch { /* noop */ }
      channelRef.current = null;
    };
  }, [isAuthenticated, isSessionValid, isValidating, qc]);

  return query;
};

// Hulp: aggregator gebruikt door initial fetch
function aggregateRows(rows: RawRow[]): OrdersDecoprofRow[] {
  const byOrder = new Map<
    string,
    {
      orderid: number | null;
      suppliers: Set<string>;
      firstNonExcludedSupplier: string | null;
      status: OrderDecoprofStatus;
      createdAt: string | null;
      voornaam_klant: string | null;
      achternaam_klant: string | null;
      emailadres_klant: string | null;
      telefoonnummer_klant: string | null;
      customer_number: string | null;
      shipment_title: string | null;
      shippingdate: string | null;
      billing_address: BillingAddress | null;
      opmerking_klant: string | null;
    }
  >();

  for (const row of rows) {
    if (!row.ordernumber) continue;
    // FASE 2 punt 1 — pickup-rijen niet meer hier filteren; UI-toggle beslist.


    const supplier = row.suppliertitle?.trim() || null;

    let entry = byOrder.get(row.ordernumber);
    const rowStatus = (row.orders_decoprof_status as OrderDecoprofStatus) || "Nieuw";
    if (!entry) {
      entry = {
        orderid: row.orderid ?? null,
        suppliers: new Set<string>(),
        firstNonExcludedSupplier: null,
        status: rowStatus,
        createdAt: row.createdat ?? null,
        voornaam_klant: row.voornaam_klant ?? null,
        achternaam_klant: row.achternaam_klant ?? null,
        emailadres_klant: row.emailadres_klant ?? null,
        telefoonnummer_klant: row.telefoonnummer_klant ?? null,
        customer_number: row.customer_number ?? null,
        shipment_title: row.shipment_title ?? null,
        shippingdate: row.shippingdate ?? null,
        billing_address: row.billing_address ?? null,
        opmerking_klant: row.opmerking_klant ?? null,
      };
      byOrder.set(row.ordernumber, entry);
    } else {
      if (entry.orderid == null && row.orderid != null) entry.orderid = row.orderid;
      entry.status = maxOrderDecoprofStatus(entry.status, rowStatus);
      // Vul ontbrekende klant-velden vanaf eerste regel die ze heeft
      entry.voornaam_klant ??= row.voornaam_klant ?? null;
      entry.achternaam_klant ??= row.achternaam_klant ?? null;
      entry.emailadres_klant ??= row.emailadres_klant ?? null;
      entry.telefoonnummer_klant ??= row.telefoonnummer_klant ?? null;
      entry.customer_number ??= row.customer_number ?? null;
      entry.shipment_title ??= row.shipment_title ?? null;
      entry.shippingdate ??= row.shippingdate ?? null;
      entry.billing_address ??= row.billing_address ?? null;
      entry.opmerking_klant ??= row.opmerking_klant ?? null;
    }

    if (supplier) {
      entry.suppliers.add(supplier);
      if (
        !entry.firstNonExcludedSupplier &&
        !ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(supplier)
      ) {
        entry.firstNonExcludedSupplier = supplier;
      }
    }
  }

  const result: OrdersDecoprofRow[] = [];
  for (const [ordernumber, entry] of byOrder.entries()) {
    const hasIncludedSupplier = Array.from(entry.suppliers).some(
      (s) => !ORDERS_DECOPROF_EXCLUDED_SUPPLIERS.has(s),
    );
    if (!hasIncludedSupplier) continue;
    const primary = entry.firstNonExcludedSupplier;
    result.push({
      ordernumber,
      orderid: entry.orderid,
      primarySupplier: primary,
      suppliers: Array.from(entry.suppliers),
      type: mapSupplierToType(primary),
      status: entry.status,
      createdAt: entry.createdAt,
      voornaam_klant: entry.voornaam_klant,
      achternaam_klant: entry.achternaam_klant,
      emailadres_klant: entry.emailadres_klant,
      telefoonnummer_klant: entry.telefoonnummer_klant,
      customer_number: entry.customer_number,
      shipment_title: entry.shipment_title,
      shippingdate: entry.shippingdate,
      billing_address: entry.billing_address,
      opmerking_klant: entry.opmerking_klant,
    });
  }
  return result.sort(compareOrdersByCreatedAtDesc);
}

/**
 * R52: kleine hook die luistert naar het flash-event en een set van te
 * highlighten ordernummers exporteert. Tegel-component gebruikt dit om de
 * fade-in animatie ~1.5s te tonen.
 */
export const useOrdersDecoprofFlash = (): Set<string> => {
  const [flashing, setFlashing] = useState<Set<string>>(new Set());
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent).detail as { ordernumber?: string } | undefined;
      const ordernumber = detail?.ordernumber;
      if (!ordernumber) return;
      setFlashing((prev) => {
        const next = new Set(prev); next.add(ordernumber); return next;
      });
      setTimeout(() => {
        setFlashing((prev) => {
          if (!prev.has(ordernumber)) return prev;
          const next = new Set(prev); next.delete(ordernumber); return next;
        });
      }, 1500);
    };
    window.addEventListener("orders-decoprof:flash", handler);
    return () => window.removeEventListener("orders-decoprof:flash", handler);
  }, []);
  return flashing;
};

export const useUpdateOrdersDecoprofStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      ordernumber?: string;
      ordernumbers?: string[];
      status: OrderDecoprofStatus;
    }) => {
      const list = params.ordernumbers && params.ordernumbers.length > 0
        ? params.ordernumbers
        : params.ordernumber
          ? [params.ordernumber]
          : [];
      if (list.length === 0) return { ordernumbers: [] as string[], status: params.status };
      const { error } = await supabase
        .from("orders_live_ls")
        .update({ orders_decoprof_status: params.status })
        .in("ordernumber", list);
      if (error) throw error;
      return { ordernumbers: list, status: params.status };
    },
    onSuccess: (result) => {
      if (!result || result.ordernumbers.length === 0) return;
      qc.setQueryData<OrdersDecoprofRow[]>(QUERY_KEY, (prev) =>
        (prev ?? []).map((row) =>
          result.ordernumbers.includes(row.ordernumber)
            ? { ...row, status: result.status }
            : row,
        ),
      );
    },
  });
};

// ---------------------------------------------------------------------------
// useOrdersDecoprofAfgerond
// Inzicht Jason: een order is "Afgerond" zodra de Lightspeed-status één van de
// completed-varianten is (packed_completed, completed_shipped,
// completed_picked_up). Naast die LS-statussen tonen we ook orders waar de
// handmatige orders_decoprof_status expliciet op 'Afgerond' is gezet.
// Hook is alleen enabled wanneer de UI het Afgerond-filter aanzet (lazy fetch).
// ---------------------------------------------------------------------------

const AFGEROND_LS_STATUSES = [
  "packed_completed",
  "completed_shipped",
  "completed_picked_up",
] as const;

const AFGEROND_QUERY_KEY = ["orders-decoprof-afgerond"] as const;

export const useOrdersDecoprofAfgerond = (enabled: boolean) => {
  const { isAuthenticated, isSessionValid, isValidating } = useAuth();
  const sinceIsoRef = useRef(new Date(Date.now() - SIXTY_DAYS_MS).toISOString());

  return useQuery({
    queryKey: AFGEROND_QUERY_KEY,
    staleTime: 30_000,
    enabled: enabled && isAuthenticated && isSessionValid && !isValidating,
    queryFn: async (): Promise<OrdersDecoprofRow[]> => {
      const excludedList = Array.from(ORDERS_DECOPROF_EXCLUDED_SUPPLIERS);
      const excludedCsv = `(${excludedList.map((s) => `"${s}"`).join(",")})`;
      const completedCsv = `(${AFGEROND_LS_STATUSES.map((s) => `"${s}"`).join(",")})`;

      const { data, error } = await supabase
        .from("orders_live_ls")
        .select(
          "orderid, ordernumber, suppliertitle, producttitle, orders_decoprof_status, createdat, status, voornaam_klant, achternaam_klant, emailadres_klant, telefoonnummer_klant, customer_number, shipment_title, shippingdate, billing_address, opmerking_klant",
        )
        .gte("createdat", sinceIsoRef.current)
        .not("suppliertitle", "is", null)
        .neq("suppliertitle", "")
        .neq("producttitle", "No products")
        .not("suppliertitle", "in", excludedCsv)
        .or(
          `status.in.${completedCsv},orders_decoprof_status.eq.Afgerond`,
        )
        .order("createdat", { ascending: false })
        .limit(MAX_VISIBLE_ORDERS);

      if (error) {
        console.error("[useOrdersDecoprofAfgerond] Error:", error);
        throw error;
      }

      const rows = (data ?? []) as RawRow[];
      // Forceer status = "Afgerond" zodat het bestaande statusFilter (= Afgerond)
      // in OrdersDecoprofTab deze rijen matched.
      return aggregateRows(rows).map((row) => ({ ...row, status: "Afgerond" as OrderDecoprofStatus }));
    },
  });
};

